import styles from "./Style.module.css";
import CalendarIcon from "../Icons/CalendarIcon";
import UserIcon from "../Icons/UserIcon";
import WebBookingIcon from "../Icons/WebBookingIcon";
import PhoneIcon from "../Icons/PhoneIcon";
import WhatsappIcon from "../Icons/WhatsappIcon";
import WalkingIcon from "../Icons/WalkingIcon";
import { MAX_RESCHEDULE_COUNT } from "../../Utils/Constants/constants";

/** Parses backend strings like "2026-04-09 16:12:03" */
function parseBookingDate(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** e.g. April 9, 2026 */
function formatBookingCalendarDate(value) {
  const d = parseBookingDate(value);
  if (!d) return value ?? "";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** e.g. on Thu, Apr 9, 4.12 PM — or without "on" when used under a "Created on" label */
function formatBookingOnTimestamp(value, includeOnPrefix = true) {
  const d = parseBookingDate(value);
  if (!d) return value ? (includeOnPrefix ? `on ${value}` : value) : "";
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  let hour12 = d.getHours();
  const minutes = d.getMinutes();
  const period = hour12 >= 12 ? "PM" : "AM";
  hour12 = hour12 % 12;
  if (hour12 === 0) hour12 = 12;
  const mm = String(minutes).padStart(2, "0");
  const formatted = `${weekday}, ${month} ${day}, ${hour12}.${mm} ${period}`;
  return includeOnPrefix ? `on ${formatted}` : formatted;
}

function getSourceDisplay(source, rescheduleCount) {
  switch (source) {
    case "website": {
      const count = Number(rescheduleCount) || 0;
      const label =
        count === 0 ? "Online" : `Reschedule ${count}/${MAX_RESCHEDULE_COUNT}`;
      return { Icon: WebBookingIcon, label };
    }
    case "WhatsApp":
      return { Icon: WhatsappIcon, label: "Source" };
    case "Walk-in":
      return { Icon: WalkingIcon, label: "Source" };
    case "By Phone":
      return { Icon: PhoneIcon, label: "Source" };
    default:
      return null;
  }
}

/** Returns a trimmed name, or null for null/empty/"null"/"undefined" */
function getDisplayName(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (s === "" || ["null", "undefined"].includes(s.toLowerCase())) return null;
  return s;
}

const BookingDetails = ({
  heading,
  CreateDate,
  CreatedBy,
  UpdatedDate,
  LastUpdatedBy,
  BookingDate,
  Source,
  rescheduleCount,
  isProductSale = false,
  Status,
}) => {
  const primaryRaw = BookingDate || UpdatedDate || CreateDate;
  const formattedPrimaryCalendar = formatBookingCalendarDate(primaryRaw);
  const createdByName = getDisplayName(CreatedBy);
  const updatedByName = getDisplayName(LastUpdatedBy);
  const formattedCreateOn = formatBookingOnTimestamp(CreateDate, !!createdByName);
  const formattedLastOn = formatBookingOnTimestamp(UpdatedDate || CreateDate, !!updatedByName);
  const sourceDisplay =
    heading === "Booking Details" ? getSourceDisplay(Source, rescheduleCount) : null;
  const SourceIcon = sourceDisplay?.Icon;
  const isRefundDetails = heading === "Refund Details";
  const createdLabel = createdByName ? `Created by ${createdByName}` : "Created on";
  const lastActionPrefix = isRefundDetails ? "Refunded" : "Last updated";
  const lastLabel = updatedByName? `${lastActionPrefix} by ${updatedByName}`: `${lastActionPrefix} on`;

  return (
    <div className={styles.bookingDetails}>
      <h3 className={styles.title}>{heading}</h3>
      <div className={styles.row}>
        <div className={styles.colLeft}>
          <CalendarIcon size={24} strokeColor="var(--neutral-900)" />
        </div>
        <div className={styles.colRight}>
          <p>{formattedPrimaryCalendar}</p>
        </div>
      </div>

      {!isRefundDetails && (
        <div className={styles.row}>
          <div className={styles.colLeft}>
            <UserIcon size={24} color="var(--neutral-900)" />
          </div>
          <div className={styles.colRight}>
            <p>{createdLabel}</p>
            <p>{formattedCreateOn}</p>
          </div>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.colLeft}>
          <UserIcon size={24} color="var(--neutral-900)" />
        </div>
        <div className={styles.colRight}>
          <p>{lastLabel}</p>
          <p>{formattedLastOn}</p>
        </div>
      </div>

      {sourceDisplay && SourceIcon && (
        <div className={styles.row}>
          <div className={styles.colLeft}>
            <SourceIcon size={20} color="var(--neutral-900)" />
          </div>
          <div className={styles.colRight}>
            <p>{sourceDisplay.label}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
